import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { Feld, Fehler } from '../components/ui';
import type { Me } from '../App';

const ROLES = ['ADMIN', 'GUTACHTER', 'MITARBEITER', 'LESER'];

export function AdminUsersPage({ me }: { me: Me }) {
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({ email: '', displayName: '', role: 'MITARBEITER', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [resetInfo, setResetInfo] = useState<{ user: string; link: string; expiresAt: string } | null>(null);
  const [mfaReset, setMfaReset] = useState<{ id: string; email: string } | null>(null);
  const [mfaGrund, setMfaGrund] = useState('');
  const [mfaDone, setMfaDone] = useState<string | null>(null);

  const load = () => api.get<any[]>('/api/v1/users').then(setUsers).catch(() => setUsers([]));
  useEffect(() => {
    void load();
  }, []);

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/api/v1/users', form);
      setForm({ email: '', displayName: '', role: 'MITARBEITER', password: '' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  async function resetToken(user: any) {
    setError(null);
    try {
      const res = await api.post(`/api/v1/users/${user.id}/reset-token`);
      setResetInfo({
        user: user.email,
        link: `${window.location.origin}${res.resetPath}`,
        expiresAt: res.expiresAt,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  async function mfaZuruecksetzen(e: FormEvent) {
    e.preventDefault();
    if (!mfaReset) return;
    setError(null);
    try {
      await api.post(`/api/v1/users/${mfaReset.id}/mfa-reset`, { begruendung: mfaGrund });
      setMfaDone(mfaReset.email);
      setMfaReset(null);
      setMfaGrund('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  return (
    <>
      <h1>{t('admin.users.title')}</h1>
      <p className="erklaerung">{t('admin.users.intro')}</p>
      {me.organization.emailDomain && (
        <p className="erklaerung">{t('admin.users.domainHint', { domain: me.organization.emailDomain })}</p>
      )}
      <Fehler code={error} />
      {resetInfo && (
        <div className="hinweis">
          <p>{t('admin.users.resetCreated', { user: resetInfo.user })}</p>
          <p>
            <code>{resetInfo.link}</code>
          </p>
        </div>
      )}
      {mfaDone && (
        <div className="hinweis">
          <p>{t('admin.users.mfaResetDone', { user: mfaDone })}</p>
        </div>
      )}
      {mfaReset && (
        <form className="karte" onSubmit={mfaZuruecksetzen}>
          <h2>{t('admin.users.mfaResetTitle', { user: mfaReset.email })}</h2>
          <p className="erklaerung">{t('admin.users.mfaResetHint')}</p>
          <Feld
            label={t('admin.users.mfaResetReason')}
            required
            hint={t('admin.users.mfaResetReasonHint')}
          >
            <textarea
              value={mfaGrund}
              onChange={(e) => setMfaGrund(e.target.value)}
              minLength={10}
              maxLength={500}
              rows={3}
              required
            />
          </Feld>
          <div className="aktionen">
            <button type="submit" className="knopf-gefahr">
              {t('common.confirm')}
            </button>
            <button
              type="button"
              className="knopf-sekundaer"
              onClick={() => {
                setMfaReset(null);
                setMfaGrund('');
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
      <div className="tabelle-scroll">
      <table className="daten">
        <thead>
          <tr>
            <th>{t('auth.email')}</th>
            <th>{t('admin.users.name')}</th>
            <th>{t('admin.users.role')}</th>
            <th>MFA</th>
            <th>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.displayName}</td>
              <td>
                <span className="badge">{t(`role.${u.role}`)}</span>
              </td>
              <td>{t(`admin.users.mfaStatus.${u.mfaStatus ?? 'AUS'}`)}</td>
              <td>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="knopf-sekundaer knopf-klein"
                    onClick={() => resetToken(u)}
                  >
                    {t('admin.users.resetAction')}
                  </button>
                  {u.id !== me.userId && u.mfaStatus !== 'AUS' && (
                    <button
                      type="button"
                      className="knopf-sekundaer knopf-klein"
                      onClick={() => {
                        setMfaDone(null);
                        setMfaGrund('');
                        setMfaReset({ id: u.id, email: u.email });
                      }}
                    >
                      {t('admin.users.mfaReset')}
                    </button>
                  )}
                  {u.id !== me.userId && (
                    <button
                      type="button"
                      className="knopf-gefahr knopf-klein"
                      onClick={async () => {
                        await api.del(`/api/v1/users/${u.id}`).catch(() => undefined);
                        await load();
                      }}
                    >
                      {t('admin.users.deactivate')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <form className="karte" onSubmit={createUser} style={{ marginTop: '1.2rem' }}>
        <h2>{t('admin.users.add')}</h2>
        <div className="feld-zeile">
          <Feld label={t('auth.email')} required>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </Feld>
          <Feld label={t('admin.users.name')} required>
            <input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              required
            />
          </Feld>
        </div>
        <div className="feld-zeile">
          <Feld label={t('admin.users.role')} required hint={t('admin.users.roleHint')}>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`role.${r}`)}
                </option>
              ))}
            </select>
          </Feld>
          <Feld label={t('admin.users.initialPassword')} required hint={t('auth.setup.passwordHint')}>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              minLength={12}
              required
            />
          </Feld>
        </div>
        <div className="aktionen">
          <button type="submit" className="knopf-primaer">
            {t('common.add')}
          </button>
        </div>
      </form>
    </>
  );
}
